<template>
  <!-- HEADER -->
  <div class="header">
    <div class="container-fluid">
      <!-- Table -->
      <div class="card mt-5">
        <div class="card-header">
          <!-- Title -->
          <h4 class="card-header-title">User Management Mitra</h4>
          <!-- Action Buttons -->
          <div class="card-header-action">
            <b-button
              class="btn-pill"
              variant="primary"
              @click="addData()">
              <span class="fe fe-user-plus"></span>
              Add User Mitra
            </b-button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="card-body text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <p class="mt-2">Loading users...</p>
        </div>

        <!-- Table -->
        <div v-else class="card-body">
          <!-- Search Filter (single field -> 'search') -->
          <div class="row mb-4">
            <div class="col-md-5 mb-3">
              <label class="form-label">Search</label>
              <input
                type="text"
                class="form-control"
                placeholder="Search iSafe, Name, NIK, or Company"
                v-model="filters.search"
                @input="onFilterChange">
            </div>
            <div class="col-md-4 mb-3">
              <label class="form-label">Company</label>
              <v-select
                v-model="filters.company"
                :options="companies"
                placeholder="All Companies"
                :clearable="true"
                @input="onFilterChange">
              </v-select>
            </div>
            <div class="col-md-3 mb-3 d-flex align-items-end justify-content-end">
              <button
                type="button"
                class="btn btn-primary"
                @click="clearFilters">
                <i class="fe fe-x me-1"></i>
                Clear Filters
              </button>
            </div>
          </div>

          <vue-good-table
            :columns="columns"
            :rows="userMitraData"
            :totalRows="totalRecords"
            :sort-options="{
              enabled: true
            }"
            :pagination-options="{
              enabled: true,
              mode: 'remote',
              position: 'bottom',
              perPage: body.limit,
              setCurrentPage: body.page,
              perPageDropdown: [10, 25, 50, 100],
              dropdownAllowAll: false,
              nextLabel: 'next',
              prevLabel: 'prev',
              rowsPerPageLabel: 'Rows per page',
              ofLabel: 'of',
              pageLabel: 'page',
              allLabel: 'All'
            }"
            @on-page-change="onPageChange"
            @on-per-page-change="onPerPageChange"
            style-class="vgt-table border-0 table">
            <!-- Empty State -->
            <div slot="emptystate" class="text-center font-size-14">
              No users found
            </div>

            <!-- Custom Rows -->
            <template slot="table-row" slot-scope="props">
              <!-- No -->
              <span v-if="props.column.field == 'no'">
                {{ (body.page - 1) * body.limit + props.index + 1 }}
              </span>

              <!-- iSafe Number -->
              <span v-if="props.column.field == 'isafe_no'">
                <span class="badge badge-primary">{{ props.row.isafe_no || '-' }}</span>
              </span>

              <!-- Name -->
              <span v-if="props.column.field == 'fullname'">
                {{ props.row.fullname }}
              </span>

              <!-- NIK -->
              <span v-if="props.column.field == 'nik'">
                {{ props.row.nik }}
              </span>

              <!-- Company -->
              <span v-if="props.column.field == 'company'">
                {{ props.row.company || '-' }}
              </span>

              <!-- Role -->
              <span v-if="props.column.field == 'partner_role'">
                <span class="bg-red-200" >
                  {{ partnerRole[props.row?.partner_role] || '-'}}
                </span>
              </span>

              <!-- Last Sync -->
              <span v-if="props.column.field == 'last_sync'">
                {{ formatDate(props.row.last_sync_at) }}
              </span>

              <!-- Action -->
              <span v-if="props.column.field == 'action'">
                <div class="d-flex gap-2">
                  <button
                    class="btn btn-sm btn-warning"
                    @click="editData(props.row)"
                    title="Edit">
                    <i class="fe fe-edit-2"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-danger"
                    @click="deleteData(props.row.nik)"
                    title="Delete">
                    <i class="fe fe-trash-2"></i>
                  </button>
                </div>
              </span>
            </template>
          </vue-good-table>
        </div>
      </div>

      <!-- Modal Add -->
      <b-modal 
        id="modal-add-user-mitra" 
        centered 
        hide-footer 
        title="Add User Mitra"
        size="lg">
        <form @submit.prevent="saveAdd()">
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label>Search User by NIK <span class="text-danger">*</span></label>
                <v-select
                  v-validate="'required'"
                  name="User"
                  v-model="selectedUser"
                  :options="userOptions"
                  :reduce="option => option"
                  label="name"
                  placeholder="Search by NIK..."
                  @search="onSearchUser"
                  @input="onUserSelected"
                  :filterable="false"
                  :loading="isSearchingUser">
                  <template #option="option">
                    <div class="d-flex align-items-center">
                      <div>
                        <div><strong>{{ option.name }}</strong></div>
                        <small>NIK: {{ option.id_number }} | iSafe: {{ option.register_number }}</small>
                      </div>
                    </div>
                  </template>
                  <template #no-options>
                    Type NIK to search...
                  </template>
                </v-select>
                <span
                  class="text-sm text-danger"
                  v-show="errors.has('User')"
                  >{{ errors.first("User") }}</span>
              </div>
            </div>

            <!-- User Card -->
            <div v-if="selectedUser" class="col-md-12">
              <div class="card mb-3">
                <div class="card-body">
                  <div class="d-flex align-items-start">
                    <div class="user-avatar me-3">
                      <!-- <img :src="`${imageBaseUrl}/attachments/${selectedUser.photo_path}`" alt=""> -->
                      <div class="avatar-circle">
                        <i class="fe fe-user"></i>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <div class="d-flex justify-content-between align-items-start">
                        <div>
                          <h5 class="mb-1">{{ selectedUser.name }}</h5>
                          <p class="text-muted mb-2">{{ selectedUser.position }}</p>
                        </div>
                        <span class="badge badge-primary">{{ selectedUser.isafe_no }}</span>
                      </div>
                      <div class="user-details mt-3">
                        <div class="row">
                          <div class="col-md-6">
                            <div class="detail-item">
                              <strong>NIK</strong>
                              <p>{{ selectedUser.nik }}</p>
                            </div>
                          </div>
                          <div class="col-md-6">
                            <div class="detail-item">
                              <strong>DoB</strong>
                              <p>{{ selectedUser.date_of_birth || '-' }}</p>
                            </div>
                          </div>
                          <div class="col-md-6">
                            <div class="detail-item">
                              <strong>DoH</strong>
                              <p>{{ selectedUser.date_of_hire || '-' }}</p>
                            </div>
                          </div>
                          <div class="col-md-6">
                            <div class="detail-item">
                              <strong>EMAIL</strong>
                              <p>{{ selectedUser.email || '-' }}</p>
                            </div>
                          </div>
                          <div class="col-md-6">
                            <div class="detail-item">
                              <strong>PHONE</strong>
                              <p>{{ selectedUser.contact || '-' }}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Role Selection -->
            <div v-if="selectedUser" class="col-md-12">
              <div class="form-group">
                <label>Role <span class="text-danger">*</span></label>
                <v-select
                  v-validate="'required'"
                  name="Role"
                  v-model="bodyAdd.partner_role"
                  :options="roleOptions"
                  :reduce="option => option.value"
                  label="label"
                  placeholder="Select Role">
                </v-select>
                <span
                  class="text-sm text-danger"
                  v-show="errors.has('Role')"
                  >{{ errors.first("Role") }}</span>
              </div>
            </div>
          </div>
          
          <div class="form-group text-right mt-4">
            <button
              type="button"
              class="btn btn-secondary me-2"
              @click="$bvModal.hide('modal-add-user-mitra')">
              Cancel
            </button>
            <button
              class="btn btn-danger"
              type="submit"
              :disabled="isLoading || !selectedUser">
              <span
                v-if="isLoading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"></span>
              Save
            </button>
          </div>
        </form>
      </b-modal>

      <!-- Modal Edit -->
      <b-modal 
        id="modal-edit-user-mitra" 
        centered 
        hide-footer 
        title="Edit User Mitra"
        size="lg">
        <form @submit.prevent="saveUpdate()">
          <div class="row">
            <!-- User Card -->
            <div class="col-md-12">
              <div class="card mb-3">
                <div class="card-body">
                  <div class="d-flex align-items-start">
                    <div class="user-avatar me-3">
                      <div class="avatar-circle">
                        <i class="fe fe-user"></i>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <div class="d-flex justify-content-between align-items-start">
                        <div>
                          <h5 class="mb-1">{{ bodyUpdate.full_name }}</h5>
                          <p class="text-muted mb-2">{{ bodyUpdate.jabatan || '-' }}</p>
                        </div>
                        <span class="badge badge-primary">{{ bodyUpdate.isafe_no }}</span>
                      </div>
                      <div class="user-details mt-3">
                        <div class="row">
                          <div class="col-md-6">
                            <div class="detail-item">
                              <strong>NIK</strong>
                              <p>{{ bodyUpdate.nik }}</p>
                            </div>
                          </div>
                          <div class="col-md-6">
                            <div class="detail-item">
                              <strong>DoB</strong>
                              <p>{{ bodyUpdate.date_of_birth || '-' }}</p>
                            </div>
                          </div>
                          <div class="col-md-6">
                            <div class="detail-item">
                              <strong>DoH</strong>
                              <p>{{ bodyUpdate.date_of_hire || '-' }}</p>
                            </div>
                          </div>
                          <div class="col-md-6">
                            <div class="detail-item">
                              <strong>EMAIL</strong>
                              <p>{{ bodyUpdate.email || '-' }}</p>
                            </div>
                          </div>
                          <div class="col-md-6">
                            <div class="detail-item">
                              <strong>PHONE</strong>
                              <p>{{ bodyUpdate.contact || '-' }}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Role Selection (Editable) -->
            <div class="col-md-12">
              <div class="form-group">
                <label>Role <span class="text-danger">*</span></label>
                <v-select
                  v-validate="'required'"
                  name="Role"
                  v-model="bodyUpdate.partner_role"
                  :options="roleOptions"
                  :reduce="option => option.value"
                  label="label"
                  placeholder="Select Role">
                </v-select>
                <span
                  class="text-sm text-danger"
                  v-show="errors.has('Role')"
                  >{{ errors.first("Role") }}</span>
              </div>
            </div>
          </div>
          
          <div class="form-group text-right mt-4">
            <button
              type="button"
              class="btn btn-secondary me-2"
              @click="$bvModal.hide('modal-edit-user-mitra')">
              Cancel
            </button>
            <button
              class="btn btn-danger"
              type="submit"
              :disabled="isLoading">
              <span
                v-if="isLoading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"></span>
              Update
            </button>
          </div>
        </form>
      </b-modal>
    </div>
  </div>
</template>

<script>
import logic from './user-management-mitra.logic.js';

export default {
  ...logic
}
</script>

<style src="./user-management-mitra.css" scoped></style>
